import React, { useState } from 'react';
import axios from 'axios';
import { LogIn } from 'lucide-react';

import { useAuthStore } from '@/core/auth';
import { CONFIG } from '@/core/config';

function loginErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    if (error.response?.status === 401) return 'Wrong email or password.';
    const apiMessage = (error.response?.data as { message?: string } | undefined)?.message;
    if (apiMessage) return apiMessage;
    if (!error.response) return 'Cannot reach the HitFactor.pro server.';
  }
  if (error instanceof Error && error.message) return error.message;
  return 'Sign-in failed. Please try again.';
}

export const LoginScreen: React.FC = () => {
  const login = useAuthStore((s) => s.login);
  const continueAsGuest = useAuthStore((s) => s.continueAsGuest);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      await login(email.trim(), password);
    } catch (err) {
      setError(loginErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex items-center justify-center w-screen h-screen bg-background text-foreground">
      <div className="w-full max-w-sm p-8 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
        <div className="mb-6 text-center">
          <h1 className="text-xl font-semibold">HitFactor.pro Stage Designer</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Sign in with your HitFactor.pro account
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex flex-col gap-1">
            <label htmlFor="login-email" className="text-xs font-medium text-gray-700 dark:text-gray-300">
              Email
            </label>
            <input
              id="login-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              autoFocus
              className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label htmlFor="login-password" className="text-xs font-medium text-gray-700 dark:text-gray-300">
              Password
            </label>
            <input
              id="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            />
          </div>

          {error && (
            <p role="alert" className="text-sm text-red-600 dark:text-red-400">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={isSubmitting || !email || !password}
            className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            <LogIn size={16} />
            {isSubmitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700 text-center">
          <button
            onClick={continueAsGuest}
            className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 underline underline-offset-2"
          >
            Continue without an account
          </button>
          <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
            Without an account, designs save as JSON files instead of to the server.
          </p>
          <a
            href={`${CONFIG.HITFACTOR_URL}/register`}
            target="_blank"
            rel="noreferrer"
            className="mt-2 inline-block text-xs text-blue-600 dark:text-blue-400 hover:underline"
          >
            Create a HitFactor.pro account
          </a>
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;
